import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MobileChartCard } from '../components/dashboard/MobileChartCard'
import { MobileMetricCard } from '../components/dashboard/MobileMetricCard'
import { 
  Activity,
  Network,
  GitBranch,
  Shield,
  Globe,
  BarChart3,
  Map,
  Search,
  Layers
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  NetworkTopology3D,
  GeographicNetworkOverlay,
  NetworkHealthMonitor,
} from '../components/network';
import type {
  NetworkNode,
  NetworkConnection,
  NetworkHealthData,
  GeographicNode,
  GeographicConnection,
} from '../components/network';

type VisualizationMode = '3d' | 'geographic' | 'health';

function generateMockNetworkData() {
  const regions = ['us-east-1', 'us-west-2', 'eu-west-1', 'ap-south-1'];
  const nodeTypes = ['leader', 'follower', 'candidate', 'observer', 'gateway'] as const;
  const statuses = ['healthy', 'degraded', 'unhealthy', 'offline'] as const;
  
  const nodes: NetworkNode[] = [];
  const geoNodes: GeographicNode[] = [];
  
  // Generate fewer nodes for mobile performance
  for (let i = 0; i < 30; i++) {
    const id = `node-${i.toString().padStart(3, '0')}`;
    const region = regions[Math.floor(Math.random() * regions.length)];
    const type = nodeTypes[Math.floor(Math.random() * nodeTypes.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    
    const regionCoords = {
      'us-east-1': { lat: 39.0458, lon: -76.6413 },
      'us-west-2': { lat: 45.8696, lon: -119.6880 },
      'eu-west-1': { lat: 53.4084, lon: -8.2439 },
      'ap-south-1': { lat: 19.0760, lon: 72.8777 },
    };
    
    const baseCoords = regionCoords[region as keyof typeof regionCoords];
    const lat = baseCoords.lat + (Math.random() - 0.5) * 5;
    const lon = baseCoords.lon + (Math.random() - 0.5) * 10;

    nodes.push({
      id,
      label: `${type.slice(0, 4)}-${i}`,
      type,
      status,
      position: {
        x: (Math.random() - 0.5) * 20,
        y: (Math.random() - 0.5) * 20,
        z: (Math.random() - 0.5) * 20,
      },
      metrics: {
        cpu: Math.random() * 100,
        memory: Math.random() * 100,
        network: Math.random() * 100,
        consensus: Math.random() * 100,
        connections: Math.floor(Math.random() * 10),
        messageRate: Math.random() * 1000,
        errorRate: Math.random() * 5,
      },
      connections: [], // Will be populated later
      lastSeen: new Date(),
      region,
      datacenter: `${region}-dc-${Math.floor(Math.random() * 3) + 1}`,
      tags: [],
    });

    geoNodes.push({
      id,
      label: `${type.slice(0, 4)}-${i}`,
      latitude: lat,
      longitude: lon,
      type: ['datacenter', 'edge', 'cloud', 'gateway'][Math.floor(Math.random() * 4)] as 'datacenter' | 'edge' | 'cloud' | 'gateway',
      provider: ['aws', 'gcp', 'azure', 'private'][Math.floor(Math.random() * 4)] as 'aws' | 'gcp' | 'azure' | 'private',
      region,
      status,
      metrics: {
        cpu: Math.random() * 100,
        memory: Math.random() * 100,
        network: Math.random() * 100,
        connections: Math.floor(Math.random() * 50),
      },
      capacity: {
        max: 1000,
        current: Math.floor(Math.random() * 1000),
      },
    });
  }

  // Generate connections
  const connections: NetworkConnection[] = [];
  const geoConnections: GeographicConnection[] = [];
  const connectionSet = new Set<string>(); // Track unique connections
  
  for (let i = 0; i < 60; i++) {
    const source = nodes[Math.floor(Math.random() * nodes.length)];
    const target = nodes[Math.floor(Math.random() * nodes.length)];
    
    if (source.id !== target.id) {
      // Create a unique key that's the same for both directions
      const connectionKey = [source.id, target.id].sort().join('-');
      
      // Skip if we already have this connection
      if (connectionSet.has(connectionKey)) {
        continue;
      }
      
      connectionSet.add(connectionKey);
      
      connections.push({
        id: `conn-${source.id}-${target.id}`,
        source: source.id,
        target: target.id,
        latency: Math.random() * 50 + 5,
        bandwidth: Math.random() * 1000,
        status: ['active', 'degraded', 'failed'][Math.floor(Math.random() * 3)] as 'active' | 'degraded' | 'failed',
        messageRate: Math.random() * 100,
        direction: 'bidirectional' as const,
      });

      geoConnections.push({
        source: source.id,
        target: target.id,
        latency: Math.random() * 50 + 5,
        bandwidth: Math.random() * 1000,
        status: ['active', 'degraded', 'failed'][Math.floor(Math.random() * 3)] as 'active' | 'degraded' | 'failed',
        type: ['internet', 'private', 'vpn', 'satellite'][Math.floor(Math.random() * 4)] as 'internet' | 'private' | 'vpn' | 'satellite',
        messageRate: Math.random() * 100,
      });
    }
  }

  // Populate node connections arrays based on the connections we created
  connections.forEach(conn => {
    const sourceNode = nodes.find(n => n.id === conn.source);
    const targetNode = nodes.find(n => n.id === conn.target);
    
    if (sourceNode && !sourceNode.connections.includes(conn.target)) {
      sourceNode.connections.push(conn.target);
    }
    if (targetNode && !targetNode.connections.includes(conn.source)) {
      targetNode.connections.push(conn.source);
    }
  });

  // Generate health data - proper array format like desktop
  const healthData: NetworkHealthData[] = nodes.map(node => ({
    nodeId: node.id,
    timestamp: new Date(),
    metrics: {
      rtt: Math.random() * 100,
      latency: Math.random() * 200,
      bandwidth: Math.random() * 1000,
      packetLoss: Math.random() * 0.05,
      jitter: Math.random() * 50,
      connectionQuality: Math.random(),
    },
    connectionState: Math.random() > 0.9 ? 'connecting' : 
                    Math.random() > 0.95 ? 'failed' : 'connected',
    errors: Math.floor(Math.random() * 10),
    region: node.region,
  }));

  return { nodes, connections, geoNodes, geoConnections, healthData };
}

export const MobileNetworkTopology: React.FC = React.memo(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [visualizationMode, setVisualizationMode] = useState<VisualizationMode>(
    (searchParams.get('view') as VisualizationMode) || '3d'
  );
  const [expandedSection, setExpandedSection] = useState<string | null>('metrics');

  const mockData = useMemo(() => generateMockNetworkData(), []);

  const handleModeChange = useCallback((mode: VisualizationMode) => {
    setVisualizationMode(mode);
    setSearchParams({ view: mode });
  }, [setSearchParams]);

  const nodeStats = useMemo(() => {
    const statusCounts = mockData.nodes.reduce((acc, node) => {
      acc[node.status] = (acc[node.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: mockData.nodes.length,
      healthy: statusCounts.healthy || 0,
      degraded: statusCounts.degraded || 0,
      unhealthy: statusCounts.unhealthy || 0,
      offline: statusCounts.offline || 0,
    };
  }, [mockData.nodes]);

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Mobile Header */}
      <div className="sticky top-0 z-40 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white">Network Topology</h1>
              <p className="text-xs text-gray-400">Real-time network view</p>
            </div>
            <button className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors">
              <Search className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* View Mode Selector */}
        <div className="px-4 pb-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {[
              { mode: '3d' as const, label: '3D View', icon: Layers },
              { mode: 'geographic' as const, label: 'Geographic', icon: Map },
              { mode: 'health' as const, label: 'Health', icon: Activity },
            ].map(({ mode, label, icon: Icon }) => (
              <button
                key={mode}
                onClick={() => handleModeChange(mode)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  visualizationMode === mode
                    ? 'bg-accent-blue text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Metrics Section */}
        <div className="space-y-3">
          <MobileMetricCard
            title="Total Nodes"
            value={nodeStats.total}
            icon={Network}
            subtitle="Active in network"
            status="info"
            details={[
              { label: 'Healthy', value: nodeStats.healthy, color: '#22c55e' },
              { label: 'Degraded', value: nodeStats.degraded, color: '#f59e0b' },
              { label: 'Unhealthy', value: nodeStats.unhealthy, color: '#ef4444' },
              { label: 'Offline', value: nodeStats.offline, color: '#6b7280' },
            ]}
          />

          <MobileMetricCard
            title="Active Connections"
            value={mockData.connections.length}
            icon={GitBranch}
            subtitle="Network links"
            trend={{
              value: 12,
              direction: 'up',
              label: 'Increased from last hour'
            }}
            status="success"
          />

          <MobileMetricCard
            title="Network Health"
            value={`${Math.round(
              mockData.healthData.reduce((sum, h) => sum + h.metrics.connectionQuality, 0) / 
              mockData.healthData.length * 100
            )}%`}
            icon={Shield}
            subtitle="Overall system health"
            status={
              mockData.healthData.reduce((sum, h) => sum + h.metrics.connectionQuality, 0) / 
              mockData.healthData.length > 0.8 ? 'success' : 'warning'
            }
          />
        </div>

        {/* Visualization Section */}
        <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h2 className="text-lg font-semibold text-white">Network Visualization</h2>
            <p className="text-sm text-gray-400 mt-1">
              {visualizationMode === '3d' && 'Interactive 3D network topology'}
              {visualizationMode === 'geographic' && 'Geographic distribution'}
              {visualizationMode === 'health' && 'System health monitoring'}
            </p>
          </div>

          <div className="h-[400px] bg-gray-950">
            <AnimatePresence mode="wait">
              {visualizationMode === '3d' && (
                <motion.div
                  key="3d"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="w-full h-full"
                >
                  <NetworkTopology3D
                    _nodes={mockData.nodes}
                    connections={mockData.connections}
                    selectedNodeId={undefined}
                    onNodeSelect={() => {}}
                    onConnectionSelect={() => {}}
                    skipIntro={false}
                  />
                </motion.div>
              )}

              {visualizationMode === 'geographic' && (
                <motion.div
                  key="geo"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="w-full h-full"
                >
                  <GeographicNetworkOverlay
                    _nodes={mockData.geoNodes}
                    connections={mockData.geoConnections}
                    selectedNode={undefined}
                    onNodeSelect={() => {}}
                    onConnectionSelect={() => {}}
                  />
                </motion.div>
              )}

              {visualizationMode === 'health' && (
                <motion.div
                  key="health"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="w-full h-full"
                >
                  <NetworkHealthMonitor
                    healthData={mockData.healthData}
                    latencyHeatmap={[]}
                    bandwidthFlows={[]}
                    timeRange={{
                      start: new Date(Date.now() - 3600000),
                      end: new Date(),
                    }}
                    onNodeSelect={() => {}}
                    refreshInterval={5000}
                  />
                </motion.div>
              )}

            </AnimatePresence>
          </div>

          {/* Mobile Controls */}
          <div className="p-4 border-t border-gray-800">
            <p className="text-xs text-gray-500 text-center">
              {visualizationMode === '3d' && 'Pinch to zoom • Drag to rotate'}
              {visualizationMode === 'geographic' && 'Tap nodes for details'}
              {visualizationMode === 'health' && 'Swipe to view metrics'}
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <button className="p-4 bg-gray-800/50 hover:bg-gray-800 border border-gray-700 rounded-xl text-center transition-colors">
            <Globe className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <span className="text-sm text-gray-300">Export View</span>
          </button>
          <button className="p-4 bg-gray-800/50 hover:bg-gray-800 border border-gray-700 rounded-xl text-center transition-colors">
            <BarChart3 className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <span className="text-sm text-gray-300">Analytics</span>
          </button>
        </div>
      </div>
    </div>
  );
});